import { useEffect, useRef, useState } from "react";

/**
 * Custom hook to detect scroll position and determine if content is scrolled horizontally or vertically
 */
export function useScrollPosition<
  T extends React.RefObject<HTMLElement>
>(options?: { initialState?: boolean; ref?: T }) {
  const { initialState = false, ref: objectRef } = options || {};

  const [isScrolledFromRightEdge, setIsScrolledFromRightEdge] =
    useState(initialState);
  const [isScrolledFromTop, setIsScrolledFromTop] = useState(initialState);
  const [isScrollPresent, setIsScrollPresent] = useState(false);
  const containerRef = useRef(null);

  const targetRef = objectRef || containerRef;

  useEffect(() => {
    // Check if the container is scrolled horizontally and vertically
    const checkScroll = () => {
      if (targetRef.current) {
        const container = targetRef.current;

        // Calculate how far we are from the right edge
        const distanceFromRightEdge =
          container.scrollWidth - container.scrollLeft - container.clientWidth;

        // We're at the right edge if the distance is very small (allowing for rounding errors)
        const isAtRightEdge = Math.abs(distanceFromRightEdge) < 2;

        // Check if scrolled from the top
        const isScrolledDown = container.scrollTop > 0;

        // Indicate when we're scrolled away from the right edge
        setIsScrolledFromRightEdge(!isAtRightEdge);

        // Indicate when we're scrolled down from the top
        setIsScrolledFromTop(isScrolledDown);
      }
    };

    // Check if scrollbars are present
    const checkForScrollbars = () => {
      if (targetRef.current) {
        const container = targetRef.current;

        // Get the computed style to check overflow settings
        const style = window.getComputedStyle(container);

        // Check if overflow is set to a value that would show scrollbars
        const horizontalOverflow = style.overflowX || style.overflow;
        const verticalOverflow = style.overflowY || style.overflow;

        // Check if content overflows and scrollbars would be visible
        const hasHorizontalScroll =
          (horizontalOverflow === "auto" ||
            horizontalOverflow === "scroll" ||
            horizontalOverflow === "overlay") &&
          container.scrollWidth > container.clientWidth;

        const hasVerticalScroll =
          (verticalOverflow === "auto" ||
            verticalOverflow === "scroll" ||
            verticalOverflow === "overlay") &&
          container.scrollHeight > container.clientHeight;

        setIsScrollPresent(hasHorizontalScroll || hasVerticalScroll);
      }
    };

    // Add scroll event listener to container
    const container = targetRef.current;
    if (container) {
      // Set up ResizeObserver to check scroll state when dimensions change
      const resizeObserver = new ResizeObserver(() => {
        checkScroll();
        checkForScrollbars(); // Also check for scrollbars when dimensions change
      });

      resizeObserver.observe(container);
      container.addEventListener("scroll", checkScroll);

      // Set up window resize event listener to check for scrollbars
      const handleWindowResize = () => {
        checkForScrollbars();
        checkScroll();
      };
      window.addEventListener("resize", handleWindowResize);

      // Initial check
      checkScroll();
      checkForScrollbars();

      // Clean up event listener and observer on unmount
      return () => {
        resizeObserver.disconnect();
        container.removeEventListener("scroll", checkScroll);
        window.removeEventListener("resize", handleWindowResize);
      };
    }
  }, [targetRef]);

  return {
    containerRef: targetRef,
    isScrolledFromRightEdge,
    isScrolledFromTop,
    isScrollPresent,
  };
}
